import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { HealthStatus } from '@/constants/analyticsThresholds';

const STATUS_LABELS: Record<HealthStatus, string> = {
  ok: 'OK',
  warning: 'Warning',
  critical: 'Critical',
  unknown: 'Unknown',
};

export interface TopContributor {
  label: string;
  href: string;
}

export interface MetricsTableRow {
  metricName: string;
  currentValue: string | number;
  delta?: string | null;
  status: HealthStatus;
  viewHref: string;
  viewLabel: string;
  dataSourceHint?: 'api' | 'derived' | null;
  /** Optional: e.g. worst-float activity, clickable drill-through */
  topContributor?: TopContributor | null;
}

export interface MetricsTableProps {
  rows: MetricsTableRow[];
  loading?: boolean;
  /** Index of the currently selected/focused row (-1 or undefined = none) */
  selectedIndex?: number;
  /** Called when a row is clicked */
  onSelectRow?: (index: number) => void;
  className?: string;
}

const statusBadgeVariant: Record<HealthStatus, 'ok' | 'warning' | 'critical' | 'unknown'> = {
  ok: 'ok',
  warning: 'warning',
  critical: 'critical',
  unknown: 'unknown',
};

const severityDotColor: Record<HealthStatus, string> = {
  critical: 'bg-destructive',
  warning: 'bg-amber-500',
  ok: 'bg-green-500',
  unknown: 'bg-muted-foreground/30',
};

export const MetricsTable: React.FC<MetricsTableProps> = ({
  rows,
  loading = false,
  selectedIndex,
  onSelectRow,
  className,
}) => {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="w-5 py-1.5 px-0" aria-label="Severity" />
            <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Metric</th>
            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Current</th>
            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Delta</th>
            <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Status</th>
            <th className="w-14 py-1.5 px-2 text-muted-foreground font-medium text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6} className="py-3 px-2 text-muted-foreground text-xs">
                Loading…
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const isSelected = selectedIndex === i;
              return (
                <tr
                  key={i}
                  onClick={() => onSelectRow?.(i)}
                  className={cn(
                    'border-b border-border/40 transition-colors',
                    onSelectRow != null && 'cursor-pointer',
                    isSelected
                      ? 'bg-primary/8 border-l-2 border-l-primary'
                      : 'hover:bg-muted/20 border-l-2 border-l-transparent'
                  )}
                >
                  {/* Severity indicator */}
                  <td className="py-1.5 px-0 text-center">
                    <span className={cn('inline-block h-2 w-2 rounded-full', severityDotColor[row.status])} />
                  </td>
                  <td className={cn('py-1.5 px-2 font-medium', isSelected ? 'text-foreground' : 'text-foreground/90')}>
                    {row.metricName}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <span className="tabular-nums text-foreground">{row.currentValue}</span>
                    {row.topContributor && (
                      <Link
                        to={row.topContributor.href}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-primary hover:underline block mt-0.5 truncate max-w-[180px] text-right"
                        title={row.topContributor.label}
                      >
                        {row.topContributor.label}
                      </Link>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">{row.delta ?? '—'}</td>
                  <td className="py-1.5 px-2">
                    <Badge variant={statusBadgeVariant[row.status]} size="sm">{STATUS_LABELS[row.status]}</Badge>
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <Link
                      to={row.viewHref}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-end gap-0.5 text-primary hover:underline text-[11px] font-medium"
                      title={row.viewLabel}
                    >
                      View
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};
